import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
  Injector,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DndDragImageOffsetFunction, DndDropzoneDirective } from 'ngx-drag-drop';
import { cloneDeep } from 'lodash-es';
import { select, Store } from '@ngxs/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { Animations } from 'src/app/core/angular-animations/angular-animations';
import { ModuleNumbers } from 'src/app/core/enums/module-numbers.enum';
import { MenuItemModel } from 'src/app/core/models/menu-id.model';
import { MenuListLogicService } from 'src/app/core/services/menu-list-logic.service';
import { MenuStructureNavigationService } from 'src/app/core/services/menu-structure-navigation.service';
import {
  ResetCurrentFilter,
  RestoreCurrentFilter,
} from 'src/app/core/state/main-table-filter.actions';
import { MainTableFilterState } from 'src/app/core/state/main-table-filter.state';
import {
  OpenMenuItem,
  CloseMenuItem,
  RestoreMenuSelection,
  SelectMenuStructureNode,
} from 'src/app/core/state/menu-tree-structure.actions';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { MenuDragDropService } from '../menu-drag-drop.service';

@Component({
  selector: 'app-menu-tree-single',
  templateUrl: './menu-tree-single.component.html',
  styleUrls: ['./menu-tree-single.component.scss'],
  animations: [Animations.toggleContainer, Animations.rotatedState],
  standalone: false,
})
export class MenuTreeSingleComponent implements OnInit, OnChanges, OnDestroy {
  private readonly injector = inject(Injector);

  private readonly currentModuleNumberFromStore = select(
    MenuTreeStructureState.getCurrentModuleNumber
  );

  @Input() item: MenuItemModel;
  @Input() onlyAdd = false;
  @Input() showFunctionalButtons = true;
  @Input() menuItemsAsRouting = false;
  @Input() menuMode = false;
  @Input() mainModules = false;
  @Input() tenantId: string;
  @Input() module: number;
  @Input() nextItem?: MenuItemModel;
  @Input() isLast = false;
  @Input() moduleNumber: ModuleNumbers;
  @Input() chosenElementId = '';
  @Input() isTopItem = false;
  @Input() adminPrivilege: string;

  @ViewChildren(DndDropzoneDirective) dropZones: QueryList<DndDropzoneDirective>;
  @ViewChild('tdzEl', { read: DndDropzoneDirective }) tdz: DndDropzoneDirective;
  @ViewChild('bdzEl', { read: DndDropzoneDirective }) bdz: DndDropzoneDirective;
  @ViewChild('dzEl', { read: DndDropzoneDirective }) dz: DndDropzoneDirective;

  @Output() readonly removeParent: EventEmitter<string> = new EventEmitter<string>();
  @Output() readonly chosenElementClicked: EventEmitter<string> = new EventEmitter<string>();

  hasChildren = false;
  showChildren = false;
  isChosen = false;

  // eslint-disable-next-line no-magic-numbers
  truncateThreshold = 75;
  private subscription = new Subscription();
  private openedIdsSub?: Subscription;
  private currentModuleNumber: number;
  private openedMenuItemIds: string[] = [];
  private isInAdmin: boolean;

  constructor(
    private store: Store,
    public menuDNDService: MenuDragDropService,
    private menuListLogicService: MenuListLogicService,
    private menuStructureNavigationService: MenuStructureNavigationService
  ) {
    this.subscription.add(
      this.menuDNDService.dragStopped.subscribe(() => {
        this.resetDropzones();
      })
    );
  }

  ngOnInit(): void {
    this.isInAdmin = this.menuListLogicService.isCurrentUrlInAdmin();
    this.isElementChosen();
    this.subscription.add(
      toObservable(this.currentModuleNumberFromStore, { injector: this.injector }).subscribe(
        (moduleNumber: number) => {
          this.currentModuleNumber = moduleNumber;
          this.subscribeOpenedMenuItemIds();
        }
      )
    );
    if (this.shouldUseLazyExpand()) {
      this.showChildren = false;
    }
  }

  ngOnChanges(): void {
    this.hasChildren = this.checkIfRowHasChildren();
    this.isElementChosen();
    if (this.isInAdmin && !this.shouldUseLazyExpand()) {
      this.subscribeOpenedMenuItemIds();
      this.showChildren = this.openedMenuItemIds.includes(this.item.id);
    }
  }

  ngOnDestroy(): void {
    this.openedIdsSub?.unsubscribe();
    this.subscription.unsubscribe();
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.showChildren = !this.showChildren;
    if (this.isInAdmin && !this.shouldUseLazyExpand()) {
      const module = this.module ?? this.currentModuleNumber;
      const action = this.showChildren
        ? new OpenMenuItem(module, this.item.id)
        : new CloseMenuItem(module, this.item.id);
      this.store.dispatch(action);
    }
  }

  routeTo(): void {
    if (!this.menuItemsAsRouting) {
      this.chosenElementClicked.emit(this.item.id);
      return;
    }
    this.selectRoutingItem();
  }

  private async selectRoutingItem(): Promise<void> {
    const module = this.module ?? this.currentModuleNumber;
    const targetUrl = this.menuStructureNavigationService.getNodeUrl(module, this.item.id);
    if (!targetUrl) {
      return;
    }
    const transaction = this.menuStructureNavigationService.startTransaction();
    if (!transaction) {
      return;
    }
    try {
      await this.performRoutingSelection(targetUrl);
    } finally {
      this.menuStructureNavigationService.finishTransaction(transaction);
    }
  }

  private async performRoutingSelection(targetUrl: string): Promise<void> {
    const previousCurrentFilter = cloneDeep(
      this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails)
    );
    const previousActiveElementId = this.store.selectSnapshot(
      MenuTreeStructureState.activeMenuElementId
    );
    const previousTableData = cloneDeep(
      this.store.selectSnapshot(MenuTreeStructureState.getSelectedPartOfMenuStructure)
    );
    const previousDisplayName = this.store.selectSnapshot(MenuTreeStructureState.getDisplayName);
    await firstValueFrom(
      this.store.dispatch([
        new ResetCurrentFilter(),
        new SelectMenuStructureNode(this.item.id, this.item.name),
      ])
    );
    const outcome = await this.menuStructureNavigationService.navigate(targetUrl);
    if (!this.menuStructureNavigationService.isCompleted(outcome)) {
      await firstValueFrom(
        this.store.dispatch([
          new RestoreMenuSelection(previousActiveElementId, previousTableData, previousDisplayName),
          new RestoreCurrentFilter(previousCurrentFilter),
        ])
      );
      return;
    }
    if (outcome === 'same-target') {
      await firstValueFrom(this.store.dispatch(new RestoreCurrentFilter(previousCurrentFilter)));
    }
    this.chosenElementClicked.emit(this.item.id);
  }

  reactOnShowChildrenChange(showChildren: boolean): void {
    this.showChildren = showChildren;
    this.hasChildren = this.checkIfRowHasChildren();
  }

  emitElementClicked(itemId: string): void {
    this.chosenElementClicked.emit(itemId);
  }

  dragItemOffsetRight: DndDragImageOffsetFunction = (event: DragEvent, dragItem: Element) => {
    const itemStyle = window.getComputedStyle(dragItem);
    const paddingTop = parseFloat(itemStyle.paddingTop) || 0;
    const paddingLeft = parseFloat(itemStyle.paddingLeft) || 0;
    const borderTop = parseFloat(itemStyle.borderTopWidth) || 0;
    const borderLeft = parseFloat(itemStyle.borderLeftWidth) || 0;

    const x = dragItem.clientWidth - (event.offsetX + paddingLeft + borderLeft);
    return {
      x,
      y: event.offsetY + paddingTop + borderTop,
    };
  };

  onDragStart(event: DragEvent, item: MenuItemModel): void {
    this.menuDNDService.activeItem = item;
  }

  onDragEnd(): void {
    this.resetDropzones();
    this.menuDNDService.activeItem = null;
  }

  onDrop(placement: 'inside' | 'bottom' | 'top'): void {
    this.menuDNDService.onDrop(this.item, placement);
  }

  resetDropzone(dropZone: DndDropzoneDirective): void {
    dropZone?.onDragLeave(new DragEvent('dragleave'));
  }

  resetDropzones(): void {
    this.dropZones.forEach((dropZone) => {
      this.resetDropzone(dropZone);
    });
  }

  resetOtherDropzones(dropZoneToReset: DndDropzoneDirective): void {
    this.dropZones.forEach((dropZone) => {
      if (dropZoneToReset !== dropZone) dropZone.onDragLeave(new DragEvent('dragleave'));
    });
  }

  private subscribeOpenedMenuItemIds(): void {
    if (!this.isInAdmin || this.shouldUseLazyExpand()) {
      return;
    }
    const module = this.module ?? this.currentModuleNumber;
    if (module === undefined || module === null) {
      return;
    }
    this.openedIdsSub?.unsubscribe();
    const openedSig = this.store.selectSignal(
      MenuTreeStructureState.getOpenedMenuItemIdsForModule(module)
    );
    this.openedIdsSub = toObservable(openedSig, { injector: this.injector }).subscribe((ids) => {
      this.openedMenuItemIds = ids;
      this.showChildren = this.openedMenuItemIds.includes(this.item.id);
    });
  }

  private isElementChosen(): void {
    this.isChosen = this.isElementChosenRecursively(this.item);
  }

  private checkIfRowHasChildren(): boolean {
    return this.item.children && this.item.children.length > 0;
  }

  private shouldUseLazyExpand(): boolean {
    return this.menuMode && this.menuItemsAsRouting;
  }

  // recursively check if an item or any of its children is chosen
  private isElementChosenRecursively(item: MenuItemModel): boolean {
    if (item.id === this.chosenElementId) return true;
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => this.isElementChosenRecursively(child));
    }
    return false;
  }
}
